import React, { Fragment } from 'react';
import { Button, RadioGroup, FormControlLabel, Typography, FormGroup } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Formik, FastField } from 'formik';
import PropTypes from 'prop-types';
import { isNil, filter, includes, omit, append, without, isEmpty } from 'ramda';

import { isBlank } from 'utils/helperMethods';
import validationSchema from 'forms/loanOfficerProfileForm';
import UserPresenter from 'presenters/UserPresenter';
import AddressSelect from 'sharedComponents/AddressSelect';
import Radio from 'sharedComponents/Radio';
import Checkbox from 'sharedComponents/Checkbox';
import Loader from 'sharedComponents/Loader';
import AccountStatus from './components/AccountStatus';
import CompanyLogo from './components/CompanyLogo';
import BaseInput from './components/BaseInput';
import PhoneInput from './components/PhoneInput';
import ProfileVideo from './components/ProfileVideo';
import Avatar from './components/Avatar';
import useStyles from './useStyles';

const AGREE_KEEP_UPDATED = 'agree';
const DISAGREE_KEEP_UPDATE = 'disagree';

const ProfileForm = props => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    onSubmit,
    currentUser,
    onRequestProfileReview,
    offerings,
    onSaveProfileVideo,
    isLoadingOfferings,
    saveProfileImage,
    saveCompanyLogo,
    isProfileImageSaving,
    isProfileCompanyLogoSaving,
  } = props;

  const fields = [
    {
      groupId: 0,
      group: [
        {
          id: 'firstName',
          name: 'firstName',
          label: t('components.ProfileForm.fields.firstName'),
        },
        {
          id: 'lastName',
          name: 'lastName',
          label: t('components.ProfileForm.fields.lastName'),
        },
        {
          id: 'profileVideoUrl',
          name: 'profileVideoUrl',
          label: t('components.ProfileForm.fields.video'),
        },
        {
          id: 'phoneNumber',
          name: 'phoneNumber',
          label: t('components.ProfileForm.fields.phoneNumber'),
        },
        {
          id: 'email',
          name: 'email',
          label: t('components.ProfileForm.fields.email'),
        },
        {
          id: 'applicationUrl',
          name: 'applicationUrl',
          label: t('components.ProfileForm.fields.applicationUrl'),
        },
        {
          id: 'nmls',
          name: 'nmls',
          label: t('components.ProfileForm.fields.nmls'),
        },
      ],
    },
    {
      groupId: 1,
      group: [
        {
          id: 'mortgageCompany',
          name: 'mortgageCompany',
          label: t('components.ProfileForm.fields.mortgageCompany'),
        },
        {
          id: 'companyLogo',
          name: 'companyLogo',
          label: t('components.ProfileForm.fields.companyLogo'),
        },
        {
          id: 'companyNmls',
          name: 'companyNmls',
          label: t('components.ProfileForm.fields.companyNmls'),
        },
        {
          id: 'stateLicenseNumber',
          name: 'stateLicenseNumber',
          label: t('components.ProfileForm.fields.stateLicenseNumber'),
        },
        {
          id: 'mortgageBidDisclosure',
          name: 'mortgageBidDisclosure',
          label: t('components.ProfileForm.fields.mortgageBidDisclosure'),
          multiline: true,
        },
        {
          id: 'aboutMe',
          name: 'aboutMe',
          label: t('components.ProfileForm.fields.aboutMe'),
          multiline: true,
        },
      ],
    },
    {
      groupId: 2,
      group: [
        {
          id: 'address',
          name: 'address',
          label: t('components.ProfileForm.fields.address'),
        },
        {
          id: 'streetAddress',
          name: 'streetAddress',
          label: t('components.ProfileForm.fields.streetAddress'),
          disabled: true,
        },
        {
          id: 'city',
          name: 'city',
          label: t('components.ProfileForm.fields.city'),
          disabled: true,
        },
        {
          id: 'state',
          name: 'state',
          label: t('components.ProfileForm.fields.state'),
          disabled: true,
        },
        {
          id: 'zipCode',
          name: 'zipCode',
          label: t('components.ProfileForm.fields.zipCode'),
          disabled: true,
        },
      ],
    },
  ];

  const currentValues = UserPresenter.loanOfficerProfileFields(currentUser);

  const {
    verificationStatus,
    verificationStatusReason,
    firstName,
    profileImageUrl,
    companyLogoUrl,
    profileVideoUrl,
  } = currentValues;

  const prepareInitialValues = values => {
    const filteredValuesFormNil = filter(i => !isNil(i), values);

    let result = { ...filteredValuesFormNil };

    const { address } = result;
    if (address) {
      const { city, state, zipCode, streetAddress1, streetAddress2 } = address;
      result = {
        ...result,
        address,
        streetAddress: filter(value => !isBlank(value), [streetAddress1, streetAddress2]).join(' '),
        city: city || '',
        state: state || '',
        zipCode: zipCode || '',
      };
    }
    const initialValueKeepUpdated = result.keepUpdated ? AGREE_KEEP_UPDATED : DISAGREE_KEEP_UPDATE;
    result = { ...result, keepUpdated: initialValueKeepUpdated };

    const omitedValues = omit(
      ['verificationStatus', 'verificationStatusReason', 'profileImageUrl', 'companyLogoUrl', 'profileVideoUrl'],
      result,
    );
    result = omitedValues;

    return result;
  };

  const handleChangeOfferings = (e, values, setFieldValue) => {
    const { checked, value } = e.target;
    if (checked && !includes(value, values.offerings)) {
      setFieldValue('offerings', append(value, values.offerings));
    } else {
      setFieldValue('offerings', without([value], values.offerings));
    }
  };

  const saveProfileVideo = async attr => {
    await onSaveProfileVideo(attr);
  };

  return (
    <div className={classes.container}>
      <AccountStatus
        status={verificationStatus}
        reason={verificationStatusReason}
        onRequestProfileReview={onRequestProfileReview}
      />
      <Avatar onFinish={saveProfileImage} value={profileImageUrl} alt={firstName} isLoading={isProfileImageSaving} />
      <Formik
        initialValues={prepareInitialValues(currentValues)}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnBlur
        validateOnChange={false}
      >
        {({ handleSubmit, errors, values, handleChange, setFieldValue }) => {
          const checkedAgree = values.keepUpdated === AGREE_KEEP_UPDATED;
          const checkedDisagree = values.keepUpdated === DISAGREE_KEEP_UPDATE;

          return (
            <form onSubmit={handleSubmit} autoComplete="off" noValidate>
              <div className={classes.groups}>
                {fields.map(({ groupId, group }, index) => {
                  const isLastGroup = fields.length - 1 === index;
                  return (
                    <div key={groupId} className={classes.formGroup}>
                      {group.map(({ id, name, label, multiline, disabled }) => {
                        if (name === 'profileVideoUrl')
                          return (
                            <ProfileVideo
                              key={id}
                              label={label}
                              value={profileVideoUrl}
                              saveProfileVideo={saveProfileVideo}
                            />
                          );
                        if (name === 'phoneNumber')
                          return <FastField name={name} label={label} key={id} id={id} component={PhoneInput} />;

                        if (name === 'companyLogo')
                          return (
                            <CompanyLogo
                              key={id}
                              label={label}
                              value={companyLogoUrl}
                              onFinish={saveCompanyLogo}
                              isLoading={isProfileCompanyLogoSaving}
                            />
                          );
                        if (name === 'address')
                          return <FastField label={label} key={id} component={AddressSelect} name={name} />;
                        return (
                          <FastField
                            name={name}
                            label={label}
                            key={id}
                            id={id}
                            component={BaseInput}
                            multiline={multiline}
                            disabled={disabled}
                          />
                        );
                      })}
                      {isLastGroup && (
                        <div className={classes.offeringsBlocks}>
                          {isLoadingOfferings ? (
                            <Loader />
                          ) : (
                            <Fragment>
                              <Typography className={classes.label} variant="body2">
                                {t('components.ProfileForm.fields.offerings')}
                              </Typography>
                              <FormGroup className={classes.checkboxGroup}>
                                {offerings.map(({ label, value }) => (
                                  <FormControlLabel
                                    key={value}
                                    control={
                                      <Checkbox
                                        checked={includes(value, values.offerings)}
                                        onChange={e => handleChangeOfferings(e, values, setFieldValue)}
                                        name={value}
                                        value={value}
                                      />
                                    }
                                    label={
                                      <Typography variant="body2" className={classes.text}>
                                        {label}
                                      </Typography>
                                    }
                                  />
                                ))}
                              </FormGroup>
                            </Fragment>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className={classes.updatesBlock}>
                <Typography className={classes.label} variant="body2">
                  {t('components.ProfileForm.fields.keepUpdated')}
                </Typography>
                <Typography variant="body1" className={classes.text}>
                  {t('components.ProfileForm.updatesText')}
                </Typography>
                <RadioGroup name="keepUpdated" onChange={handleChange} className={classes.radioGroup}>
                  <FormControlLabel
                    className={classes.radioGroupLabel}
                    checked={checkedAgree}
                    value={AGREE_KEEP_UPDATED}
                    control={<Radio />}
                    label={
                      <Typography variant="body2" className={classes.text}>
                        {t('components.ProfileForm.checkboxAgreeText')}
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    className={classes.radioGroupLabel}
                    checked={checkedDisagree}
                    value={DISAGREE_KEEP_UPDATE}
                    control={<Radio />}
                    label={
                      <Typography variant="body2" className={classes.text}>
                        {t('components.ProfileForm.checkboxDisagreeText')}
                      </Typography>
                    }
                  />
                </RadioGroup>
              </div>
              <div className={classes.buttonContainer}>
                <Button className={classes.button} color="primary" type="submit" disabled={!isEmpty(errors)}>
                  {t('components.ProfileForm.buttons.saveChanges')}
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

ProfileForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  currentUser: UserPresenter.shape().isRequired,
  onRequestProfileReview: PropTypes.func.isRequired,
  offerings: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onSaveProfileVideo: PropTypes.func.isRequired,
  isLoadingOfferings: PropTypes.bool.isRequired,
  saveProfileImage: PropTypes.func.isRequired,
  saveCompanyLogo: PropTypes.func.isRequired,
  isProfileImageSaving: PropTypes.bool.isRequired,
  isProfileCompanyLogoSaving: PropTypes.bool.isRequired,
};

export default ProfileForm;



// WEBPACK FOOTER //
// ./src/components/LoanOfficerSettings/components/Profile/components/ProfileForm/ProfileForm.js